import React from 'react';
import { useTheme } from '../../context/ThemeContext';
import { 
  Timer, BarChart3, Trophy, DollarSign, Phone,
  Eye, Download, Moon, Zap, Users, Target, Activity
} from 'lucide-react';

export default function Features() {
  const { isDark } = useTheme();

  const features = [
    {
      icon: Timer,
      title: "Timer intelligent",
      description: "Calcule automatiquement le temps actif en excluant les pauses",
      color: "blue"
    },
    {
      icon: BarChart3,
      title: "KPI en temps réel",
      description: "Appels, leads, conversions, taux - tout est synchronisé live",
      color: "purple"
    },
    {
      icon: Trophy,
      title: "Leaderboard performance",
      description: "Classement des top performers avec podium",
      color: "yellow"
    },
    {
      icon: DollarSign,
      title: "Commissions automatiques",
      description: "Calcul instantané des commissions basé sur les conversions",
      color: "green"
    },
    {
      icon: Phone,
      title: "Tracking appels",
      description: "Historique complet, durée, statut de chaque appel",
      color: "indigo"
    },
    {
      icon: Eye,
      title: "Supervision live",
      description: "Voyez en temps réel ce que fait chaque agent",
      color: "pink"
    },
    {
      icon: Download,
      title: "Export Excel",
      description: "Exportez toutes les statistiques en un clic",
      color: "orange"
    },
    {
      icon: Moon,
      title: "Dark mode",
      description: "Interface élégante jour/nuit pour tous les goûts",
      color: "gray"
    }
  ];

  const getColorClasses = (color) => {
    const colors = {
      blue: {
        bg: 'bg-blue-100 dark:bg-blue-900/30',
        text: 'text-blue-600 dark:text-blue-400',
        gradient: 'from-blue-500 to-blue-600'
      },
      purple: {
        bg: 'bg-purple-100 dark:bg-purple-900/30',
        text: 'text-purple-600 dark:text-purple-400',
        gradient: 'from-purple-500 to-purple-600'
      },
      yellow: {
        bg: 'bg-yellow-100 dark:bg-yellow-900/30',
        text: 'text-yellow-600 dark:text-yellow-400',
        gradient: 'from-yellow-500 to-yellow-600'
      },
      green: {
        bg: 'bg-green-100 dark:bg-green-900/30',
        text: 'text-green-600 dark:text-green-400',
        gradient: 'from-green-500 to-green-600'
      },
      indigo: {
        bg: 'bg-indigo-100 dark:bg-indigo-900/30',
        text: 'text-indigo-600 dark:text-indigo-400',
        gradient: 'from-indigo-500 to-indigo-600'
      },
      pink: {
        bg: 'bg-pink-100 dark:bg-pink-900/30',
        text: 'text-pink-600 dark:text-pink-400',
        gradient: 'from-pink-500 to-pink-600'
      },
      orange: {
        bg: 'bg-orange-100 dark:bg-orange-900/30',
        text: 'text-orange-600 dark:text-orange-400',
        gradient: 'from-orange-500 to-orange-600'
      },
      gray: {
        bg: 'bg-gray-100 dark:bg-gray-800',
        text: 'text-gray-600 dark:text-gray-400',
        gradient: 'from-gray-500 to-gray-600'
      }
    };
    return colors[color] || colors.blue;
  };

  return (
    <section id="features" className="py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-500/10 border border-primary-500/20 mb-6">
            <Zap className="w-4 h-4 text-primary-600" />
            <span className="text-sm font-medium text-primary-600 dark:text-primary-400">
              Tout ce dont vous avez besoin
            </span>
          </div>
          <h2 className={`text-3xl lg:text-4xl font-bold mb-4 ${
            isDark ? 'text-white' : 'text-gray-900'
          }`}>
            Une solution complète pour vos équipes
          </h2>
          <p className={`text-lg ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            Supervision, performance, commissions - tout est intégré
          </p>
        </div>

        {/* Grille de fonctionnalités */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {features.map((feature, index) => {
            const Icon = feature.icon;
            const colors = getColorClasses(feature.color);
            
            return (
              <div 
                key={index}
                className={`group p-6 rounded-2xl border transition-all duration-300 hover:-translate-y-1 hover:shadow-xl ${
                  isDark 
                    ? 'bg-gray-900/50 border-gray-800 hover:border-gray-700' 
                    : 'bg-white border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className={`w-12 h-12 rounded-xl ${colors.bg} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                  <Icon className={`w-6 h-6 ${colors.text}`} />
                </div>
                <h3 className={`text-lg font-semibold mb-2 ${
                  isDark ? 'text-white' : 'text-gray-900'
                }`}>
                  {feature.title}
                </h3>
                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  {feature.description}
                </p>
              </div>
            );
          })}
        </div>

        {/* Badge temps réel */}
        <div className={`mt-12 p-6 rounded-2xl border ${
          isDark ? 'bg-gray-900 border-gray-800' : 'bg-gray-50 border-gray-200'
        }`}>
          <div className="flex items-center justify-center gap-2 text-center">
            <Activity className="w-5 h-5 text-primary-600" />
            <p className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              🔄 Synchronisation Supabase en temps réel — toutes les données sont live
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}